import { type ReactNode } from "react";
import * as Dialog from "@radix-ui/react-dialog";

import { icons } from "~/ui";
import { tw } from "~/utils";

interface ResponsiveModalProps {
  children: ReactNode;
  className?: string;
  show: boolean;
  title: string;
  hideTitle?: boolean;
  onClose: () => void;
}

/**
 * Responsive modal built on Radix Dialog.
 *
 * - Mobile (< md): bottom sheet that slides up from the bottom, with a visual
 *   drag handle and a close (X) button in the top-right corner.
 * - Desktop (>= md): side drawer that slides in from the right, full height,
 *   with a back button in the top-left corner.
 *
 * The viewport switch is handled purely with Tailwind responsive variants, so
 * there is no flash or resize re-render. Dismissed via tap-outside, the mobile
 * close button or the desktop back button.
 */
export const ResponsiveModal = ({
  children,
  className,
  show,
  title,
  hideTitle = false,
  onClose,
}: ResponsiveModalProps) => (
  <Dialog.Root
    open={show}
    onOpenChange={(open) => {
      if (!open) onClose();
    }}
  >
    <Dialog.Portal>
      <Dialog.Overlay className="backdrop-blur-xs fixed inset-0 z-40 bg-black/30 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
      <Dialog.Content
        className={tw(
          // shared
          "fixed z-40 flex flex-col bg-white shadow-lg duration-300 data-[state=open]:animate-in data-[state=closed]:animate-out",
          // mobile: bottom sheet
          "inset-x-0 bottom-0 max-h-[90dvh] w-full rounded-t-3xl px-6 pb-8 pt-3",
          "max-md:data-[state=closed]:slide-out-to-bottom max-md:data-[state=open]:slide-in-from-bottom",
          // desktop: right-side drawer
          "md:inset-x-auto md:inset-y-0 md:right-0 md:h-full md:max-h-none md:w-[550px] md:max-w-xl md:rounded-none md:px-8 md:pb-10 md:pt-8",
          "md:data-[state=closed]:slide-out-to-right md:data-[state=open]:slide-in-from-right",
          className,
        )}
        onPointerDownOutside={(e) => {
          if (
            e.target instanceof Element &&
            e.target.closest("[data-sonner-toast]")
          ) {
            e.preventDefault();
          }
        }}
      >
        {/* mobile: visual drag handle */}
        <div className="mx-auto mb-3 h-1.5 w-10 shrink-0 rounded-full bg-gray-300 md:hidden" />

        {/* mobile: close (X) button, top-right */}
        <Dialog.Close
          aria-label="Close"
          className="text-indigo absolute right-4 top-4 rounded-full p-1 transition-opacity hover:opacity-70 md:hidden"
        >
          <icons.outline.XMarkIcon className="size-6" />
        </Dialog.Close>

        {/* desktop: back button, top-left */}
        <Dialog.Close
          aria-label="Back"
          className="text-violet hidden w-fit items-center justify-start gap-2 text-sm font-medium md:flex"
        >
          <span className="border-violet flex size-5 items-center justify-center rounded-full border">
            <icons.outline.ArrowLongLeftIcon className="text-violet size-3 stroke-2" />
          </span>
          Back
        </Dialog.Close>

        <Dialog.Title
          className={tw(
            "font-ivy text-indigo pr-8 text-2xl font-normal leading-8 tracking-tight",
            hideTitle && "sr-only",
          )}
        >
          {title}
        </Dialog.Title>

        <div className="mt-4 flex-1 overflow-auto font-sans">{children}</div>
      </Dialog.Content>
    </Dialog.Portal>
  </Dialog.Root>
);
